import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type {
  ReleaseDetail,
  ReleaseTestCategoryView,
  ReleaseTestSummary,
} from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  LoadingState,
  StatusBadge,
} from "../../shared/ui";
import { QaSignOffDrawer } from "./QaSignOffDrawer";
import { TestCategoryDrawer } from "./TestCategoryDrawer";
import { TEST_STATUS_LABELS } from "./releaseVocabulary";

/**
 * §27 and §29: testing at the release level, and the tally underneath it.
 *
 * <p>Two things are shown because §27 asks two questions. Whether every ticket that needed testing
 * passed, and whether the QA lead considers the release fit to ship, are different — a release can
 * be green on every row and still be one somebody with the whole picture holds back.
 */
export function ReleaseTestingTab({
  release,
  onChanged,
}: {
  release: ReleaseDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [signingOff, setSigningOff] = useState(false);
  const [category, setCategory] = useState<ReleaseTestCategoryView | null>(null);
  const [addingCategory, setAddingCategory] = useState(false);

  const testing = useQuery({
    queryKey: ["release-testing", release.id],
    queryFn: () => api.get<ReleaseTestSummary>(`/api/v1/releases/${release.id}/testing`),
  });

  if (testing.isPending) {
    return <LoadingState label="Loading testing…" />;
  }
  if (testing.isError) {
    return <ErrorState error={testing.error} onRetry={() => void testing.refetch()} />;
  }

  const data = testing.data;
  const { tally } = data;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="QA sign-off"
          subtitle={
            data.approvedBy
              ? `${TEST_STATUS_LABELS[data.qaStatus]} by ${data.approvedBy.fullName}`
              : TEST_STATUS_LABELS[data.qaStatus]
          }
          action={
            can("RELEASE_QA_SIGNOFF") && (
              <Button size="sm" onClick={() => setSigningOff(true)}>
                Record decision
              </Button>
            )
          }
        />
        <CardBody className="space-y-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Tally label="Needed testing" value={tally.required} />
            <Tally label="Passed" value={tally.passed} tone="green" />
            <Tally label="Failed" value={tally.failed} tone={tally.failed > 0 ? "red" : undefined} />
            <Tally label="Blocked" value={tally.blocked} tone={tally.blocked > 0 ? "amber" : undefined} />
            <Tally label="Not tested" value={tally.notTested} />
            <Tally label="Waived" value={tally.waived} tone={tally.waived > 0 ? "violet" : undefined} />
          </div>

          {data.comments && (
            <p className="rounded-md bg-ink-50 px-3 py-2 text-sm text-ink-700">{data.comments}</p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Test categories"
          subtitle="Not every release needs every kind — and saying so is a decision (§28)."
          action={
            can("RELEASE_QA_UPDATE") && (
              <Button size="sm" variant="secondary" onClick={() => setAddingCategory(true)}>
                Add a kind
              </Button>
            )
          }
        />
        <CardBody>
          {data.categories.length === 0 ? (
            <p className="text-sm text-ink-600">
              Nothing recorded yet. Which kinds of testing does this release need — regression,
              performance, migration?
            </p>
          ) : (
            <ul className="divide-y divide-ink-100">
              {data.categories.map((entry) => (
                <li key={entry.id} className="flex items-center justify-between gap-3 py-2">
                  <div>
                    <p className="text-sm text-ink-900">
                      {entry.category.replace(/_/g, " ").toLowerCase()}
                      {!entry.required && (
                        <span className="ml-2 text-xs text-ink-500">not required</span>
                      )}
                    </p>
                    {entry.waiverReason && (
                      <p className="text-xs text-ink-600">{entry.waiverReason}</p>
                    )}
                    {entry.notes && <p className="text-xs text-ink-600">{entry.notes}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge tone={entry.required ? "gray" : "violet"}>
                      {entry.status.replace(/_/g, " ").toLowerCase()}
                    </StatusBadge>
                    {can("RELEASE_QA_UPDATE") && (
                      <Button variant="ghost" size="sm" onClick={() => setCategory(entry)}>
                        Edit
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="How it was tested" subtitle="§27's record of the testing process." />
        <CardBody className="space-y-2 text-sm">
          <Row label="QA lead" value={data.qaLead?.fullName ?? "—"} />
          <Row label="Build tested" value={data.buildTested ?? "—"} />
          <Row label="Environment" value={data.environmentTested?.name ?? "—"} />
          <Row label="Upgrade tested" value={yesNo(data.upgradeTested)} />
          <Row label="Production-like environment" value={yesNo(data.productionLikeTested)} />
          <Row label="Client / UAT complete" value={yesNo(data.clientUatCompleted)} />
          <Row label="Failures observed" value={yesNo(data.failuresObserved)} />
          {data.untestedUseCases && (
            <div className="pt-1">
              <p className="text-xs font-medium text-ink-500">Untested use cases</p>
              <p className="mt-0.5 whitespace-pre-wrap text-ink-800">{data.untestedUseCases}</p>
            </div>
          )}
        </CardBody>
      </Card>

      <TestCategoryDrawer
        release={release}
        category={category}
        open={addingCategory || category != null}
        onClose={() => {
          setAddingCategory(false);
          setCategory(null);
        }}
        onSaved={onChanged}
      />
      <QaSignOffDrawer
        release={release}
        summary={data}
        open={signingOff}
        onClose={() => setSigningOff(false)}
        onSaved={onChanged}
      />
    </div>
  );
}

/** Not answered is a different thing from "no", and shows as such. */
function yesNo(value: boolean | null): string {
  if (value == null) {
    return "Not recorded";
  }
  return value ? "Yes" : "No";
}

function Tally({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "green" | "red" | "amber" | "violet";
}) {
  const colour =
    tone === "green" ? "text-emerald-700"
      : tone === "red" ? "text-red-700"
      : tone === "amber" ? "text-amber-700"
      : tone === "violet" ? "text-violet-700"
      : "text-ink-900";
  return (
    <div className="rounded-lg bg-ink-50 px-3 py-2">
      <p className={`tnum text-lg font-semibold ${colour}`}>{value}</p>
      <p className="text-xs text-ink-500">{label}</p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-ink-500">{label}</span>
      <span className="text-ink-900">{value}</span>
    </div>
  );
}
